/**
 * Commercial Optimization Utility Functions
 * Shared utility functions for the commercial optimization components
 */

// Format currency values
export const formatCurrency = (value, options = {}) => {
  const {
    currency = 'USD',
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    locale = 'en-US'
  } = options;
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits
  }).format(value || 0);
};

// Format percentage values
export const formatPercentage = (value, decimals = 1) => {
  return `${(value || 0).toFixed(decimals)}%`;
};

// Format large numbers with suffixes
export const formatNumber = (value, options = {}) => {
  const { 
    compact = false, 
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
    locale = 'en-US'
  } = options;
  
  if (compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      minimumFractionDigits,
      maximumFractionDigits
    }).format(value);
  }
  
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(value || 0);
};

// Validate configuration object
export const validateConfig = (config) => {
  const errors = [];
  
  if (!config) {
    errors.push('Configuration object is required');
    return { isValid: false, errors };
  }
  
  // Check required sections
  const requiredSections = ['marketEnergyAllocation', 'optimalBiddingTable', 'layout', 'typography'];
  requiredSections.forEach(section => {
    if (!config[section]) {
      errors.push(`Missing required configuration section: ${section}`);
    }
  });
  
  // Validate market energy allocation config
  if (config.marketEnergyAllocation) {
    const { riskLevels, markets, colors } = config.marketEnergyAllocation;
    
    if (!Array.isArray(riskLevels) || riskLevels.length === 0) {
      errors.push('marketEnergyAllocation.riskLevels must be a non-empty array');
    }
    
    if (!Array.isArray(markets) || markets.length === 0) {
      errors.push('marketEnergyAllocation.markets must be a non-empty array');
    }
    
    if (!colors || typeof colors !== 'object') {
      errors.push('marketEnergyAllocation.colors must be an object');
    }
  }
  
  // Validate optimal bidding table config
  if (config.optimalBiddingTable) {
    const { columns } = config.optimalBiddingTable;
    
    if (!Array.isArray(columns) || columns.length === 0) {
      errors.push('optimalBiddingTable.columns must be a non-empty array');
    } else {
      columns.forEach((column, index) => {
        if (!column.key || !column.label) {
          errors.push(`optimalBiddingTable.columns[${index}] must have key and label properties`);
        }
      });
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Debounce function for performance optimization
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Deep clone utility
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    Object.keys(obj).forEach(key => {
      clonedObj[key] = deepClone(obj[key]);
    });
    return clonedObj;
  }
};

// Error handling utilities
export const createError = (message, canRetry = true, code = null) => ({
  message,
  canRetry,
  code,
  timestamp: new Date().toISOString()
});

export const handleApiError = (error) => {
  if (error.response) {
    // API responded with error status
    return createError(
      `Server error: ${error.response.data?.message || error.response.statusText}`,
      error.response.status >= 500,
      error.response.status
    );
  } else if (error.request) {
    // Network error
    return createError('Network error. Please check your connection.', true, 'NETWORK_ERROR');
  } else {
    // Other error
    return createError(error.message || 'An unexpected error occurred', false, 'UNKNOWN_ERROR');
  }
};

// Local storage utilities
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
    return false;
  }
};

export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
    return defaultValue;
  }
};

// Date and time utilities
export const formatTimestamp = (timestamp, options = {}) => {
  const {
    locale = 'en-US',
    dateStyle = 'short',
    timeStyle = 'short'
  } = options;
  
  return new Intl.DateTimeFormat(locale, {
    dateStyle,
    timeStyle
  }).format(new Date(timestamp));
};

export default {
  formatCurrency,
  formatPercentage,
  formatNumber,
  validateConfig,
  debounce,
  deepClone,
  createError,
  handleApiError,
  saveToLocalStorage,
  loadFromLocalStorage,
  formatTimestamp
};
